import type {
  PriceBreakdownData,
  PriceBreakdownRow,
} from '../components/booking-purchase-dialog/price-breakdown/PriceBreakdown';

type Dependencies = {
  money: (amount: any, currency: string) => string;
  translate: (key: string, params?: Record<string, unknown>) => string;
  formatNumber: (value: number, decimals: number) => string;
  isFeatureFlagEnabled: (flag: string) => boolean;
  getTranslatedDurationUnit: (unit: string, count: number) => string;
};

type Discount = {
  applies_to_joining_fee_only?: boolean;
  num_cycles?: number;
  rate_type?: string;
  rate_value?: number;
  discount_amount?: number;
  total_applied?: number;
};

type BreakdownOptions = {
  services?: Array<{ is_promo_code_applicable?: boolean; price?: number }>;
  currency: string;
  isRecurringMembership?: boolean;
  subscriptionDetails?: {
    isSubscription?: boolean;
    autoRenewal?: boolean;
    fixedNumberOfCycles?: boolean;
    numCycles?: number;
  };
};

function isJoiningFeeOnlyDiscount(
  discount: Discount,
  options: BreakdownOptions,
  isFeatureFlagEnabled: Dependencies['isFeatureFlagEnabled']
) {
  if (!isFeatureFlagEnabled('is-joining-fee-discount-enabled')) {
    return false;
  }
  if (!options.isRecurringMembership) {
    return false;
  }
  return discount.applies_to_joining_fee_only === true;
}

function appliesToAllFuturePayments(
  discount: Discount,
  subscription?: BreakdownOptions['subscriptionDetails']
) {
  if (discount.num_cycles === 0) {
    return true;
  }
  if (!subscription) {
    return false;
  }
  if (subscription.autoRenewal) {
    return false;
  }
  if (!subscription.fixedNumberOfCycles) {
    return false;
  }
  return subscription.numCycles! <= discount.num_cycles!;
}

function discountCyclesNote(
  discount: Discount,
  options: BreakdownOptions,
  dependencies: Dependencies
) {
  if (isJoiningFeeOnlyDiscount(discount, options, dependencies.isFeatureFlagEnabled)) {
    return dependencies.translate('WEB_APPLIED_TO_JOINING_FEE');
  }
  if (appliesToAllFuturePayments(discount, options.subscriptionDetails)) {
    return dependencies.translate('WEB_DISCOUNT_APPLIES_TO_ALL_FUTURE_PAYMENTS');
  }
  if ((discount.num_cycles || 0) > 1) {
    return dependencies.translate('WEB_DISCOUNT_APPLIES_TO_FIRST_NUM_CYCLES_PAYMENTS', {
      numCycles: discount.num_cycles,
    });
  }
  return dependencies.translate('WEB_DISCOUNT_APPLIES_TO_FIRST_PAYMENT_ONLY');
}

function discountLabel(discount: Discount, currency: string, dependencies: Dependencies) {
  const applied = dependencies.translate('WEB_DISCOUNT_APPLIED');
  if (discount.rate_type === 'percentage') {
    return `${discount.rate_value}% ${applied}`;
  }
  return `${dependencies.money(discount.rate_value, currency)} ${applied}`;
}

function totalNote(breakdown: any, translate: Dependencies['translate']) {
  if (breakdown.is_first_period_free) {
    return translate('WEB_PRICE_BREAKDOWN_FIRST_PERIOD_FREE');
  }
  if (breakdown.isProRated) {
    return translate('WEB_PRICE_BREAKDOWN_PRORATED_NET_PRICE');
  }
  return undefined;
}

export function createPriceBreakdownDataService(dependencies: Dependencies) {

  function serviceRows(breakdown: any, options: BreakdownOptions): PriceBreakdownRow[] {
    const services = options.services || [];
    const subtotalLabel = dependencies.translate('WEB_SUBSCRIPTION_SUBTOTAL');
    const joiningFeeLabel = `${dependencies.translate('WEB_JOINING_FEE')} ${dependencies.translate('WEB_SUBTOTAL')}`;
    return services
      .filter((service) => service.is_promo_code_applicable)
      .map(() => ({
        label: subtotalLabel,
        amount: dependencies.money(breakdown.subscription_net_price, options.currency),
      }))
      .concat(
        services
          .filter((service) => !service.is_promo_code_applicable)
          .map((service) => ({
            label: joiningFeeLabel,
            amount: dependencies.money(service.price, options.currency),
          }))
      );
  }

  function discountRows(breakdown: any, options: BreakdownOptions): PriceBreakdownRow[] {
    const isSubscription = !!options.subscriptionDetails?.isSubscription;
    return (breakdown.applied_discounts || []).map((discount: Discount) => ({
      label: discountLabel(discount, options.currency, dependencies),
      note: isSubscription ? discountCyclesNote(discount, options, dependencies) : undefined,
      amount: `- ${dependencies.money(
        discount.discount_amount || discount.total_applied,
        options.currency
      )}`,
      amountClassName: 'row',
    }));
  }

  function taxRows(breakdown: any, currency: string): PriceBreakdownRow[] {
    return (breakdown.applied_taxes || []).map((tax: any) => ({
      label: `${tax.name} (${dependencies.formatNumber(tax.rate, 2)}%)`,
      amount: dependencies.money(tax.price || tax.total_applied, currency),
    }));
  }

  function totalRow(breakdown: any, currency: string): PriceBreakdownRow {
    return {
      label: dependencies.translate('WEB_TOTAL_TO_PAY_TODAY'),
      note: totalNote(breakdown, dependencies.translate),
      amount: dependencies.money(breakdown.total_price, currency),
      emphasis: true,
    };
  }

  function footnotes(breakdown: any, currency: string): string[] {
    const notes: string[] = [];
    if (breakdown.maintenance_fee) {
      const fee = breakdown.maintenance_fee;
      notes.push(
        dependencies
          .translate('WEB_MAINTENANCE_FEE_MESSAGE')
          .replace('{fee_name}', fee.line_items[0].service_name)
          .replace('{amount}', `${fee.amount} ${currency}`)
          .replace('{delay_charge_days}', fee.delay_charge_days)
          .replace('{interval_count}', fee.duration_time_unit_count)
          .replace(
            '{interval_unit}',
            dependencies.getTranslatedDurationUnit(
              fee.duration_time_unit,
              fee.duration_time_unit_count
            )
          )
      );
    }
    if (dependencies.isFeatureFlagEnabled('surcharge-create')) {
      notes.push(dependencies.translate('WEB_SURCHARGE_FEE_MESSAGE'));
    }
    return notes;
  }

  function buildBreakdown(breakdown: any, options: BreakdownOptions): PriceBreakdownData {
    if (!breakdown) {
      return { rows: [], footnotes: [] };
    }
    return {
      rows: serviceRows(breakdown, options)
        .concat(discountRows(breakdown, options))
        .concat(taxRows(breakdown, options.currency))
        .concat([totalRow(breakdown, options.currency)]),
      footnotes: footnotes(breakdown, options.currency),
    };
  }

  return { buildBreakdown };
}
